import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../Servicios/supabaseClient';
import { useAuth } from '../Context/AuthContext';
import Swal from 'sweetalert2';

// Exportar ventas (comprobantes emitidos en AFIP/ARCA) al formato RG 3685:
// VENTAS.txt + ALICUOTAS.txt (el mismo que "Exportar Ventas" de RCEL) + CSV.
export default function SalesExport({ defaultCuit }) {
    const { user } = useAuth();

    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const isoDate = (d) => d.toISOString().slice(0, 10);

    const [desde, setDesde] = useState(isoDate(firstOfMonth));
    const [hasta, setHasta] = useState(isoDate(today));
    const [ptosVenta, setPtosVenta] = useState(''); // "2, 4" (vacío = todos)
    const [cuit, setCuit] = useState(defaultCuit || ''); // CUIT con clave fiscal (persona que loguea en ARCA)
    const [cuitEmpresa, setCuitEmpresa] = useState(''); // CUIT representado (empresa); vacío = se representa a sí mismo
    const [claveFiscal, setClaveFiscal] = useState(''); // solo en memoria, nunca se guarda
    const [generating, setGenerating] = useState(false);
    const [exports, setExports] = useState([]);
    const pollTimer = useRef(null);

    useEffect(() => {
        if (!user) return;
        fetchExports();
        fetchCredsDefaults();

        const channel = supabase
            .channel('public:sales_exports')
            .on('postgres_changes',
                { event: '*', schema: 'public', table: 'sales_exports', filter: `user_id=eq.${user.id}` },
                () => fetchExports()
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
            if (pollTimer.current) clearTimeout(pollTimer.current);
        };
    }, [user]);

    const fetchExports = async () => {
        const { data, error } = await supabase
            .from('sales_exports')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(10);
        if (!error) setExports(data);
    };

    // Prefill de CUIT persona (login) y CUIT empresa (representado) desde las
    // credenciales guardadas, para no tener que tipear ambos a mano.
    const fetchCredsDefaults = async () => {
        const { data } = await supabase
            .from('afip_credentials')
            .select('cuit, cuit_empresa')
            .eq('user_id', user.id)
            .single();
        if (data) {
            if (data.cuit) setCuit(data.cuit);
            if (data.cuit_empresa) setCuitEmpresa(data.cuit_empresa);
        }
    };

    // supabase.functions.invoke() ante un status != 2xx devuelve un
    // FunctionsHttpError cuyo .message es el genérico "Edge Function returned a
    // non-2xx status code". El error real de ARCA viene en el body de la
    // respuesta (error.context). Lo extraemos para mostrarlo.
    const extractFnError = async (error) => {
        try {
            if (error?.context && typeof error.context.json === 'function') {
                const body = await error.context.json();
                if (body?.error) return body.error;
            }
        } catch { /* body no legible: caemos al mensaje genérico */ }
        return error?.message || 'Falló el export';
    };

    // Si la función devolvió "procesando", seguimos consultando hasta que termine
    const pollCheck = async (exportId, attempt = 0) => {
        if (attempt > 30) {
            setGenerating(false);
            return Swal.fire('Atención', 'El export sigue en proceso. Se actualizará solo en la lista cuando termine.', 'info');
        }
        const { data, error } = await supabase.functions.invoke('sales-export', {
            body: { action: 'check', exportId },
        });
        if (error || data?.status === 'error') {
            setGenerating(false);
            const msg = error ? await extractFnError(error) : (data?.error || 'Falló el export');
            return Swal.fire('Error', msg, 'error');
        }
        if (data?.status === 'completado') {
            setGenerating(false);
            fetchExports();
            return;
        }
        pollTimer.current = setTimeout(() => pollCheck(exportId, attempt + 1), 8000);
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        setGenerating(true);
        try {
            const ptos = ptosVenta
                .split(',')
                .map((s) => parseInt(s.trim(), 10))
                .filter((n) => Number.isInteger(n) && n > 0);

            const { data, error } = await supabase.functions.invoke('sales-export', {
                body: {
                    action: 'start',
                    desde,
                    hasta,
                    cuit: cuit.replace(/\D/g, ''),
                    cuitEmpresa: cuitEmpresa.replace(/\D/g, ''),
                    claveFiscal,
                    ...(ptos.length > 0 ? { ptosVenta: ptos } : {}),
                },
            });

            if (error) throw new Error(await extractFnError(error));
            if (data?.error) throw new Error(data.error);

            if (data?.status === 'procesando') {
                fetchExports();
                pollTimer.current = setTimeout(() => pollCheck(data.exportId), 8000);
                return;
            }

            setGenerating(false);
            fetchExports();
            if (data?.warnings?.length > 0) {
                Swal.fire('Export generado con avisos', data.warnings.join('\n'), 'warning');
            }
        } catch (err) {
            setGenerating(false);
            Swal.fire('Error', err.message, 'error');
        }
    };

    const handleDownload = async (filePath) => {
        try {
            const { data, error } = await supabase.storage
                .from('exports')
                .createSignedUrl(filePath, 60);
            if (error) throw error;
            window.open(data.signedUrl, '_blank');
        } catch (err) {
            Swal.fire('Error', 'No se pudo descargar el archivo.', 'error');
        }
    };

    const fmtRango = (r) => {
        const f = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString();
        return `${f(r.desde)} – ${f(r.hasta)}`;
    };

    return (
        <div>
            <form onSubmit={handleGenerate} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div>
                    <label className="inputLabel">Desde</label>
                    <input type="date" className="inputField" value={desde} max={hasta} required
                        onChange={(e) => setDesde(e.target.value)} />
                </div>
                <div>
                    <label className="inputLabel">Hasta</label>
                    <input type="date" className="inputField" value={hasta} min={desde} required
                        onChange={(e) => setHasta(e.target.value)} />
                </div>
                <div>
                    <label className="inputLabel">Ptos. de venta (opcional)</label>
                    <input type="text" className="inputField" placeholder="Ej: 2, 4 (vacío = todos)"
                        value={ptosVenta} onChange={(e) => setPtosVenta(e.target.value)} />
                </div>
                <div>
                    <label className="inputLabel">CUIT con clave fiscal (persona)</label>
                    <input type="text" className="inputField" placeholder="20123456789" required
                        value={cuit} onChange={(e) => setCuit(e.target.value)} />
                </div>
                <div>
                    <label className="inputLabel">CUIT empresa (representado)</label>
                    <input type="text" className="inputField" placeholder="Opcional (si representás a una empresa)"
                        value={cuitEmpresa} onChange={(e) => setCuitEmpresa(e.target.value)} />
                </div>
                <div>
                    <label className="inputLabel">Clave fiscal</label>
                    <input type="password" className="inputField" placeholder="********" required
                        autoComplete="off" value={claveFiscal} onChange={(e) => setClaveFiscal(e.target.value)} />
                </div>
                <button type="submit" className="btnPrimary" disabled={generating}>
                    {generating ? 'Generando…' : 'Generar export'}
                </button>
            </form>
            <p className="muted" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
                Ingresá el CUIT de la <strong>persona</strong> que loguea en ARCA con su clave fiscal.
                Si operás en representación de una empresa, completá además el <strong>CUIT empresa</strong>.
                Tu clave fiscal se usa únicamente para esta consulta en ARCA y no se almacena.
            </p>

            <div className="tableWrap">
                {exports.length === 0 ? (
                    <p style={{ padding: '1rem', color: '#888' }}>Todavía no generaste ningún export.</p>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Generado</th>
                                <th>Período</th>
                                <th>Ptos. Vta.</th>
                                <th>Cbtes.</th>
                                <th>Estado</th>
                                <th>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {exports.map((r) => (
                                <tr key={r.id}>
                                    <td>{new Date(r.created_at).toLocaleString()}</td>
                                    <td>{fmtRango(r)}</td>
                                    <td>{r.ptos_venta?.length ? r.ptos_venta.join(', ') : 'Todos'}</td>
                                    <td>{r.comprobantes_count ?? '-'}</td>
                                    <td>
                                        <span className={`pill ${r.status === 'completado' ? 'pillOk' : r.status === 'error' ? 'pillErr' : 'pillWarn'}`}
                                            title={r.error || undefined}>
                                            {r.status === 'completado' && r.error ? 'completado ⚠' : r.status}
                                        </span>
                                    </td>
                                    <td>
                                        {r.status === 'completado' ? (
                                            <button className="btnGhost" style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                                                onClick={() => handleDownload(r.file_path)}>
                                                Descargar ZIP
                                            </button>
                                        ) : r.status === 'error' ? (
                                            <span style={{ fontSize: '0.8rem', color: '#e66' }} title={r.error}>Falló</span>
                                        ) : (
                                            <span style={{ fontSize: '0.8rem', color: '#888' }}>Generando…</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
